import { useEffect, useRef, useState } from 'react';

type PollingState<T> = {
  data: T | null;
  error: string;
  loading: boolean;
  refreshing: boolean;
};

type UsePollingResourceOptions<T> = {
  load: (signal: AbortSignal) => Promise<T>;
  intervalMs?: number;
  pollingKey?: string;
};

export function usePollingResource<T>({
  load,
  intervalMs = 10000,
  pollingKey = '',
}: UsePollingResourceOptions<T>) {
  const loadRef = useRef(load);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [state, setState] = useState<PollingState<T>>({
    data: null,
    error: '',
    loading: true,
    refreshing: false,
  });

  loadRef.current = load;

  useEffect(() => {
    let controller: AbortController | null = null;
    let isActive = true;

    const loadNow = () => {
      controller?.abort();
      controller = new AbortController();

      setState((current) => ({
        ...current,
        error: '',
        loading: !current.data,
        refreshing: Boolean(current.data),
      }));

      loadRef.current(controller.signal)
        .then((data) => {
          if (!isActive) {
            return;
          }
          setState({
            data,
            error: '',
            loading: false,
            refreshing: false,
          });
        })
        .catch((error) => {
          if (!isActive || error?.name === 'AbortError') {
            return;
          }
          setState((current) => ({
            ...current,
            error: error instanceof Error ? error.message : 'Falha ao carregar dados.',
            loading: false,
            refreshing: false,
          }));
        });
    };

    loadNow();
    const intervalId = window.setInterval(loadNow, intervalMs);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
      controller?.abort();
    };
  }, [intervalMs, pollingKey, refreshIndex]);

  return {
    ...state,
    refresh: () => setRefreshIndex((current) => current + 1),
  };
}
